import { Box } from '@mui/material';
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent,
  type PointerEvent,
} from 'react';

import {
  useBoardEditingStore,
  useBoardSelectionStore,
  useBoardViewportStore,
  useWallContentStore,
  useWallParticipantsStore,
  useWorkspaceUiStore,
  type WallBoard as WallBoardModel,
  type WallCard,
} from '@walls/store';
import { BoardCanvasLayer } from './layers/BoardCanvasLayer';
import { CardActionOverlays } from './card-actions/CardActionOverlays';
import { BoardCardsLayer } from './layers/BoardCardsLayer';
import { BoardConnectionsLayer } from './layers/BoardConnectionsLayer';
import { BoardNotification } from './layers/BoardNotification';
import { BoardSelectionOverlay } from './layers/BoardSelectionOverlay';
import { BoardViewportContext } from './viewport/BoardViewportContext';
import { useImageCardUpload } from './cards/actions/useImageCardUpload';
import { useBoardKeyboardShortcuts } from './hooks/useBoardKeyboardShortcuts';
import { useBoardPan } from './hooks/useBoardPan';
import { useExpandableBoardCanvas } from './hooks/useExpandableBoardCanvas';
import { InlineMenuOverlay } from './inline-menu/InlineMenuOverlay';
import { getBoardRenderWindow } from './viewport/renderWindow';
import { useBoardMarqueeSelection } from './selection/hooks/useBoardMarqueeSelection';
import { getBoardCardTree } from './selectors/boardCardSelectors';

interface WallBoardProps {
  board: WallBoardModel;
}

interface BoardRect {
  height: number;
  width: number;
  x: number;
  y: number;
}

interface ClusterCreateDrag {
  didDrag: boolean;
  pointerId: number;
  startPoint: {
    x: number;
    y: number;
  };
}

const clusterToolCursor =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2732%27 height=%2732%27 viewBox=%270 0 32 32%27%3E%3Cpath fill=%27%230f2a39%27 d=%27M5 8.8 14 3l9 5.8-9 5.8z%27/%3E%3Cpath fill=%27%230f2a39%27 d=%27m7.2 15.2-2.2 1.4 9 5.8 9-5.8-2.2-1.4-6.8 4.4z%27/%3E%3Cpath fill=%27white%27 stroke=%27%230f2a39%27 stroke-width=%272%27 d=%27M20 20h8M24 16v8%27/%3E%3C/svg%3E") 8 8, copy';
const clusterCreateDragThreshold = 4;
const visibleBoardRectStep = 300;

export function WallBoard({ board }: WallBoardProps) {
  const cards = useWallContentStore((state) => state.cards);
  const connections = useWallContentStore((state) => state.connections);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);
  const setSelectedTool = useWorkspaceUiStore((state) => state.setSelectedTool);
  const selectedCardColor = useWorkspaceUiStore((state) => state.selectedCardColor);
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const connectingFromId = useBoardSelectionStore((state) => state.connectingFromId);
  const setConnectingFromId = useBoardSelectionStore((state) => state.setConnectingFromId);
  const setConnectionPointer = useBoardSelectionStore((state) => state.setConnectionPointer);
  const startCreateCard = useBoardEditingStore((state) => state.startCreateCard);
  const startCreateCluster = useBoardEditingStore((state) => state.startCreateCluster);
  const setClusterDraftRect = useBoardViewportStore((state) => state.setClusterDraftRect);
  const setDropPosition = useBoardViewportStore((state) => state.setDropPosition);
  const topicCardsRef = useRef<HTMLDivElement>(null);
  const boardSpacerRef = useRef<HTMLDivElement>(null);
  const imageDragDepthRef = useRef(0);
  const clusterCreateDragRef = useRef<ClusterCreateDrag | null>(null);
  const visibleBoardRectAnimationFrameRef = useRef<number | null>(null);
  const [isImageDragActive, setIsImageDragActive] = useState(false);
  const [clusterCreatePreview, setClusterCreatePreview] = useState<BoardRect | null>(null);
  const [visibleBoardRect, setVisibleBoardRect] = useState<BoardRect | null>(null);
  const boardDimensions = useMemo(() => getBoardDimensions(cards), [cards]);
  const { canvasSize, expandCanvasNearViewportEdges, handleScroll } = useExpandableBoardCanvas(
    topicCardsRef,
    boardSpacerRef,
    boardDimensions
  );
  const panHandlers = useBoardPan(topicCardsRef, expandCanvasNearViewportEdges);
  const marqueeSelectionHandlers = useBoardMarqueeSelection(board, topicCardsRef);
  const renderWindow = useMemo(
    () => getBoardRenderWindow(visibleBoardRect),
    [visibleBoardRect]
  );
  const { uploadImageCards } = useImageCardUpload(board);
  const canvasSizePercent = 100 / zoom;
  const canvasOffset = (100 - canvasSizePercent) / 2;
  useBoardKeyboardShortcuts(board);
  const canCreateCard = Boolean(
    board.allowCardCreation &&
      (board.isOwner || (currentParticipantId && (board.isMember || board.canCollaborate)))
  );
  const canCreateCluster = canCreateCard && board.allowClustering;
  const updateVisibleBoardRect = useCallback(() => {
    const viewport = topicCardsRef.current;

    if (!viewport) {
      return;
    }

    const nextVisibleBoardRect = {
      height: viewport.clientHeight,
      width: viewport.clientWidth,
      x: Math.floor(viewport.scrollLeft / visibleBoardRectStep) * visibleBoardRectStep,
      y: Math.floor(viewport.scrollTop / visibleBoardRectStep) * visibleBoardRectStep,
    };

    setVisibleBoardRect((currentVisibleBoardRect) =>
      currentVisibleBoardRect &&
      currentVisibleBoardRect.height === nextVisibleBoardRect.height &&
      currentVisibleBoardRect.width === nextVisibleBoardRect.width &&
      currentVisibleBoardRect.x === nextVisibleBoardRect.x &&
      currentVisibleBoardRect.y === nextVisibleBoardRect.y
        ? currentVisibleBoardRect
        : nextVisibleBoardRect
    );
  }, []);
  const scheduleVisibleBoardRectUpdate = useCallback(() => {
    if (visibleBoardRectAnimationFrameRef.current != null) {
      return;
    }

    visibleBoardRectAnimationFrameRef.current = requestAnimationFrame(() => {
      visibleBoardRectAnimationFrameRef.current = null;
      updateVisibleBoardRect();
    });
  }, [updateVisibleBoardRect]);

  useLayoutEffect(() => {
    updateVisibleBoardRect();

    const viewport = topicCardsRef.current;

    if (!viewport) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(scheduleVisibleBoardRectUpdate);
    resizeObserver.observe(viewport);

    return () => {
      if (visibleBoardRectAnimationFrameRef.current != null) {
        cancelAnimationFrame(visibleBoardRectAnimationFrameRef.current);
        visibleBoardRectAnimationFrameRef.current = null;
      }
      resizeObserver.disconnect();
    };
  }, [scheduleVisibleBoardRectUpdate, updateVisibleBoardRect, zoom]);

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    imageDragDepthRef.current += 1;

    if (canCreateCard && hasImageTransfer(event.dataTransfer)) {
      setIsImageDragActive(true);
    }
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect =
      canCreateCard && hasImageTransfer(event.dataTransfer) ? 'copy' : 'none';
  };

  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) {
      return;
    }

    imageDragDepthRef.current = Math.max(0, imageDragDepthRef.current - 1);

    if (imageDragDepthRef.current === 0) {
      setIsImageDragActive(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (!hasFileTransfer(event.dataTransfer)) {
      return;
    }

    event.preventDefault();
    imageDragDepthRef.current = 0;
    setIsImageDragActive(false);

    if (!canCreateCard) {
      return;
    }

    const files = getImageFiles(event.dataTransfer);

    if (files.length === 0) {
      return;
    }

    const point = getBoardPoint(event, topicCardsRef.current, zoom);

    void uploadImageCards({
      color: selectedCardColor,
      files,
      point,
    });
  };

  const startClusterCreateDrag = (event: PointerEvent<HTMLElement>) => {
    const point = getBoardPoint(event, topicCardsRef.current, zoom);

    clusterCreateDragRef.current = {
      didDrag: false,
      pointerId: event.pointerId,
      startPoint: point,
    };
    setClusterCreatePreview(null);
    setClusterDraftRect(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const updateClusterCreateDrag = (event: PointerEvent<HTMLElement>) => {
    const currentDrag = clusterCreateDragRef.current;

    if (!currentDrag || currentDrag.pointerId !== event.pointerId) {
      return false;
    }

    const point = getBoardPoint(event, topicCardsRef.current, zoom);
    const rect = getRectFromPoints(currentDrag.startPoint, point);

    if (
      !currentDrag.didDrag &&
      (rect.width >= clusterCreateDragThreshold || rect.height >= clusterCreateDragThreshold)
    ) {
      currentDrag.didDrag = true;
    }

    setClusterCreatePreview(currentDrag.didDrag ? rect : null);
    event.preventDefault();

    return true;
  };

  const finishClusterCreateDrag = (event: PointerEvent<HTMLElement>) => {
    const currentDrag = clusterCreateDragRef.current;

    if (!currentDrag || currentDrag.pointerId !== event.pointerId) {
      return false;
    }

    const point = getBoardPoint(event, topicCardsRef.current, zoom);
    const rect = currentDrag.didDrag ? getRectFromPoints(currentDrag.startPoint, point) : null;
    const placement = rect ?? { ...currentDrag.startPoint, height: 0, width: 0 };

    clusterCreateDragRef.current = null;
    setClusterCreatePreview(null);
    setDropPosition({ x: placement.x, y: placement.y });
    setClusterDraftRect(rect);
    startCreateCluster();
    setSelectedTool('pointer');

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    event.preventDefault();

    return true;
  };

  const cancelClusterCreateDrag = (event?: PointerEvent<HTMLElement>) => {
    const currentDrag = clusterCreateDragRef.current;

    if (!currentDrag || (event && currentDrag.pointerId !== event.pointerId)) {
      return false;
    }

    clusterCreateDragRef.current = null;
    setClusterCreatePreview(null);

    if (event?.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    return true;
  };

  return (
    <Box
      id="brainstorming-container"
      className="open"
      component="section"
      aria-label="Wall board"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        bgcolor: '#f9faf9',
        height: '100%',
        position: 'relative',
        width: '100%',
        zIndex: 1,
      }}
    >
      <BoardNotification cards={cards} />
      <CardActionOverlays board={board} />
      <BoardViewportContext.Provider value={topicCardsRef}>
        <Box
          id="topic-cards"
          className="open"
          ref={topicCardsRef}
          onContextMenu={(event) => event.preventDefault()}
          onScroll={(event) => {
            handleScroll(event);
            scheduleVisibleBoardRectUpdate();
          }}
          onDoubleClick={(event) => {
            if (
              canCreateCard &&
              (selectedTool === 'pointer' || selectedTool === 'card') &&
              !isBoardElementEvent(event)
            ) {
              setDropPosition(getBoardPoint(event, topicCardsRef.current, zoom));
              startCreateCard();
            }
          }}
          onPointerDown={(event) => {
            if (connectingFromId && !isBoardElementEvent(event)) {
              setConnectionPointer(getBoardPoint(event, topicCardsRef.current, zoom));
              setConnectingFromId(null);
              event.preventDefault();
              return;
            }

            if (selectedTool === 'card' && canCreateCard && !isBoardElementEvent(event)) {
              const point = getBoardPoint(event, topicCardsRef.current, zoom);

              setDropPosition(point);
              startCreateCard();
              setSelectedTool('pointer');
              event.preventDefault();
              return;
            }

            if (selectedTool === 'cluster' && canCreateCluster && !isBoardElementEvent(event)) {
              startClusterCreateDrag(event);
              return;
            }

            marqueeSelectionHandlers.onPointerDown(event);
            if (!event.defaultPrevented) {
              panHandlers.onPointerDown(event);
            }
          }}
          onPointerMove={(event) => {
            if (updateClusterCreateDrag(event)) {
              return;
            }

            if (connectingFromId) {
              setConnectionPointer(getBoardPoint(event, topicCardsRef.current, zoom));
              event.preventDefault();
              return;
            }

            marqueeSelectionHandlers.onPointerMove(event);
            if (!event.defaultPrevented) {
              panHandlers.onPointerMove(event);
            }
          }}
          onPointerUp={(event) => {
            if (finishClusterCreateDrag(event)) {
              return;
            }

            marqueeSelectionHandlers.onPointerUp(event);
            if (!event.defaultPrevented) {
              panHandlers.onPointerUp(event);
            }
          }}
          onPointerCancel={(event) => {
            if (cancelClusterCreateDrag(event)) {
              return;
            }
          }}
          onPointerLeave={() => {
            if (connectingFromId) {
              setConnectionPointer(null);
            }

            marqueeSelectionHandlers.onPointerLeave();
            panHandlers.onPointerLeave();
          }}
          sx={{
            bottom: zoom === 1 ? 0 : `${canvasOffset}%`,
            cursor:
              selectedTool === 'hand'
                ? 'grab'
                : selectedTool === 'cluster'
                ? clusterToolCursor
                : 'default',
            left: zoom === 1 ? 0 : `${canvasOffset}%`,
            margin: 0,
            overflow: 'auto',
            overflowAnchor: 'none',
            overscrollBehavior: 'contain',
            padding: 0,
            position: 'absolute',
            right: zoom === 1 ? 0 : `${canvasOffset}%`,
            top: zoom === 1 ? 0 : `${canvasOffset}%`,
            touchAction: 'none',
            transform: zoom === 1 ? undefined : `scale(${zoom})`,
            userSelect: 'none',
            width: zoom === 1 ? '100%' : `${canvasSizePercent}%`,
            height: zoom === 1 ? '100%' : `${canvasSizePercent}%`,
            zIndex: 1,
          }}
        >
          <Box
            ref={boardSpacerRef}
            sx={{
              minHeight: canvasSize.height,
              minWidth: canvasSize.width,
              overflowAnchor: 'none',
              position: 'relative',
            }}
          >
            <BoardCanvasLayer board={board} renderWindow={renderWindow} />
            <BoardConnectionsLayer
              cards={cards}
              connections={connections}
              renderWindow={renderWindow}
            />
            <BoardCardsLayer board={board} cards={cards} visibleRect={renderWindow} />
            <InlineMenuOverlay board={board} />
            {clusterCreatePreview && (
              <Box
                aria-hidden="true"
                sx={{
                  bgcolor: 'rgba(216, 247, 255, 0.25)',
                  border: '2px dashed #0f2a39',
                  borderRadius: '8px',
                  height: clusterCreatePreview.height,
                  left: clusterCreatePreview.x,
                  pointerEvents: 'none',
                  position: 'absolute',
                  top: clusterCreatePreview.y,
                  width: clusterCreatePreview.width,
                  zIndex: 999,
                }}
              />
            )}
          </Box>
          <BoardSelectionOverlay />
        </Box>
      </BoardViewportContext.Provider>
      {isImageDragActive && (
        <Box
          aria-hidden="true"
          sx={{
            alignItems: 'center',
            bgcolor: 'rgba(31, 41, 55, 0.08)',
            border: '2px dashed rgba(31, 41, 55, 0.42)',
            color: '#1f2937',
            display: 'flex',
            fontSize: 16,
            fontWeight: 600,
            inset: 12,
            justifyContent: 'center',
            letterSpacing: 0,
            pointerEvents: 'none',
            position: 'absolute',
            zIndex: 20,
          }}
        >
          Drop images to add cards
        </Box>
      )}
    </Box>
  );
}

function getBoardPoint(
  event: PointerEvent<HTMLElement> | DragEvent<HTMLElement> | MouseEvent<HTMLElement>,
  viewport: HTMLElement | null,
  zoom: number
) {
  if (!viewport) {
    return { x: 0, y: 0 };
  }

  const rect = viewport.getBoundingClientRect();

  return {
    x: viewport.scrollLeft + (event.clientX - rect.left) / zoom,
    y: viewport.scrollTop + (event.clientY - rect.top) / zoom,
  };
}

function getRectFromPoints(start: { x: number; y: number }, end: { x: number; y: number }) {
  return {
    height: Math.abs(end.y - start.y),
    width: Math.abs(end.x - start.x),
    x: Math.min(start.x, end.x),
    y: Math.min(start.y, end.y),
  };
}

function isBoardElementEvent(event: PointerEvent<HTMLElement> | MouseEvent<HTMLElement>) {
  return event.target instanceof Element && Boolean(event.target.closest('[data-element-id]'));
}

function hasFileTransfer(dataTransfer: DataTransfer) {
  return Array.from(dataTransfer.types).includes('Files');
}

function hasImageTransfer(dataTransfer: DataTransfer) {
  const items = Array.from(dataTransfer.items ?? []);

  if (items.length > 0) {
    return items.some((item) => item.kind === 'file' && item.type.startsWith('image/'));
  }

  return getImageFiles(dataTransfer).length > 0;
}

function getImageFiles(dataTransfer: DataTransfer) {
  return Array.from(dataTransfer.files).filter((file) => file.type.startsWith('image/'));
}

function getBoardDimensions(cards: WallCard[]) {
  const { rootCards, rootClusters } = getBoardCardTree(cards);
  const rootElements = [...rootCards, ...rootClusters];

  return rootElements.reduce(
    (dimensions, card) => ({
      height: Math.max(dimensions.height, (card.yPos ?? 0) + (card.height ?? 180) + 240),
      width: Math.max(dimensions.width, (card.xPos ?? 0) + (card.width ?? 220) + 240),
    }),
    { height: window.innerHeight, width: window.innerWidth }
  );
}
